import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, Clock3, History, Send } from 'lucide-react';
import SiteBrandedLoader from '../../components/SiteBrandedLoader';
import { HAIRLINE, Panel } from '../../components/assessment/AssessmentUI';
import { PortalShell, dueLabel, timeAgo } from '../../components/portal/PortalShell';
import { FieldValue, TaskFieldInput, emptyValue } from '../../components/portal/TaskFieldInput';
import { portalService } from '../../services/api';
import { PortalAssignmentDetail, SubmissionFile } from '../../types/portal';
import { STATUS } from './PortalDashboard';

const fa = (n: number) => n.toLocaleString('fa-IR');
const CTA_BG = 'linear-gradient(135deg,#1C3B9B,#3A2FA0)';

/** وضعیت‌هایی که در آن‌ها فرم قفل است */
const LOCKED = ['SUBMITTED', 'UNDER_REVIEW', 'APPROVED'];

const PortalTaskPage: React.FC = () => {
  const { assignmentId = '' } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<PortalAssignmentDetail | null>(null);
  const [values, setValues] = useState<Record<string, FieldValue>>({});

  /** آخرین نسخه پایهٔ فرم است تا اصلاح از صفر شروع نشود */
  const hydrate = useCallback((detail: PortalAssignmentDetail) => {
    const next: Record<string, FieldValue> = {};
    for (const field of detail.task.fields) next[field.id] = emptyValue();

    for (const value of detail.latest?.values ?? []) {
      next[value.fieldId] = {
        textValue: value.textValue ?? undefined,
        numberValue:
          value.numberValue === null || value.numberValue === undefined
            ? undefined
            : Number(value.numberValue),
        dateValue: value.dateValue ?? undefined,
        jsonValue: value.jsonValue,
        files: value.files ?? [],
      };
    }
    setValues(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await portalService.getTask(assignmentId);
      setData(detail);
      hydrate(detail);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت تسک انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [assignmentId, hydrate]);

  useEffect(() => {
    load();
  }, [load]);

  const locked = useMemo(() => {
    if (!data) return true;
    return LOCKED.includes(data.status) && !data.task.allowEditAfterSubmit;
  }, [data]);

  const patch = (fieldId: string, next: Partial<FieldValue>) =>
    setValues((prev) => ({ ...prev, [fieldId]: { ...prev[fieldId], ...next } }));

  const addFile = async (fieldId: string, file: File) => {
    const uploaded = await portalService.uploadFile(assignmentId, fieldId, file);
    setValues((prev) => ({
      ...prev,
      [fieldId]: { ...prev[fieldId], files: [...prev[fieldId].files, uploaded] },
    }));
  };

  const dropFile = async (fieldId: string, fileId: string) => {
    await portalService.removeFile(fileId);
    setValues((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        files: prev[fieldId].files.filter((f) => f.id !== fileId),
      },
    }));
  };

  const submit = async () => {
    if (!data) return;
    setSaving(true);
    setError('');
    try {
      const payload = data.task.fields.map((field) => {
        const value = values[field.id] ?? emptyValue();
        return {
          fieldId: field.id,
          textValue: value.textValue,
          numberValue: value.numberValue,
          dateValue: value.dateValue,
          jsonValue: value.jsonValue,
          fileIds: value.files.map((f) => f.id),
        };
      });
      const updated = await portalService.submitTask(assignmentId, payload);
      setData(updated);
      hydrate(updated);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ثبت پاسخ انجام نشد.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFBFE]">
        <SiteBrandedLoader />
      </div>
    );
  }

  if (!data) {
    return (
      <PortalShell>
        <div className="mx-auto max-w-2xl py-10">
          <Panel className="p-10 text-center">
            <p className="text-sm font-bold text-slate-600">{error || 'تسک یافت نشد.'}</p>
            <Link
              to="/portal"
              className="mt-4 inline-block rounded-2xl px-5 py-3 text-sm font-black text-white"
              style={{ background: CTA_BG }}
            >
              بازگشت به پرتال
            </Link>
          </Panel>
        </div>
      </PortalShell>
    );
  }

  const badge = STATUS[data.status];
  const review = data.latest?.review;
  const due = data.dueAt ? new Date(data.dueAt) : null;
  const dueInfo = dueLabel(data.dueAt);
  const dueText = dueInfo?.text ?? '';
  const overdueNow = !!dueInfo?.overdue;
  const dueSoon = !!dueInfo?.soon;

  return (
    <PortalShell>
      <div className="mx-auto max-w-3xl space-y-4">
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="inline-flex items-center gap-1.5 text-[12px] font-black text-slate-500"
        >
          <ArrowRight className="h-4 w-4" />
          بازگشت به پرتال
        </button>

        <Panel className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-lg font-black text-slate-900 sm:text-xl">{data.task.title}</h1>
              {due ? (
                <p
                  className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-black"
                  style={{
                    color: overdueNow ? '#d03b3b' : dueSoon ? '#B45309' : '#94A3B8',
                  }}
                >
                  <Clock3 className="h-3.5 w-3.5" />
                  {dueText} · {due.toLocaleDateString('fa-IR')}
                </p>
              ) : null}
            </div>
            <span
              className="shrink-0 rounded-full px-3 py-1 text-[11px] font-black"
              style={{ backgroundColor: badge.color + '18', color: badge.color }}
            >
              {badge.label}
            </span>
          </div>

          {data.task.description ? (
            <p className="mt-3 whitespace-pre-line text-[12.5px] leading-8 text-slate-600">
              {data.task.description}
            </p>
          ) : null}
        </Panel>

        {review ? (
          <div
            className="rounded-2xl border p-4"
            style={{
              borderColor: review.decision === 'APPROVED' ? '#BFE6BF' : '#F4DFAE',
              backgroundColor: review.decision === 'APPROVED' ? '#F1FAF1' : '#FEF8EA',
            }}
          >
            <p className="flex items-center gap-2 text-[13px] font-black text-slate-800">
              {review.decision === 'APPROVED' ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              )}
              {review.decision === 'APPROVED' ? 'تأیید شد' : 'نیازمند اصلاح'}
              {review.score !== null && review.score !== undefined ? (
                <span className="text-slate-500">
                  · امتیاز {fa(review.score)}
                  {data.task.maxScore ? ' از ' + fa(data.task.maxScore) : ''}
                </span>
              ) : null}
            </p>
            {review.feedback ? (
              <p className="mt-2 whitespace-pre-line text-[12px] leading-7 text-slate-700">
                {review.feedback}
              </p>
            ) : null}
            <p className="mt-2 text-[10.5px] font-bold text-slate-400">
              {[review.reviewer?.firstName, review.reviewer?.lastName].filter(Boolean).join(' ')}
            </p>
          </div>
        ) : null}

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] font-bold leading-7 text-red-700">
            {error}
          </div>
        ) : null}

        <Panel className="space-y-5 p-5">
          {data.task.fields.map((field) => (
            <TaskFieldInput
              key={field.id}
              field={field}
              value={values[field.id] ?? emptyValue()}
              disabled={locked}
              onChange={(next) => patch(field.id, next)}
              onUpload={(file) => addFile(field.id, file)}
              onRemoveFile={(fileId) => dropFile(field.id, fileId)}
              onDownloadFile={(file: SubmissionFile) =>
                portalService.downloadFile(file.id, file.originalName)
              }
            />
          ))}

          {locked ? (
            <p className="rounded-xl border px-4 py-3 text-center text-[12px] font-bold text-slate-500" style={{ borderColor: HAIRLINE }}>
              {data.status === 'APPROVED'
                ? 'این تمرین تأیید شده و دیگر قابل ویرایش نیست.'
                : 'پاسخ شما ثبت شده و در انتظار بررسی منتور است.'}
            </p>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={submit}
              className="flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3.5 text-sm font-black text-white transition hover:-translate-y-0.5 disabled:opacity-50"
              style={{ background: CTA_BG }}
            >
              <Send className="h-4 w-4" />
              {saving
                ? 'در حال ثبت…'
                : data.status === 'NEEDS_REVISION'
                  ? 'ارسال نسخهٔ اصلاح‌شده'
                  : 'ثبت پاسخ'}
            </button>
          )}
        </Panel>

        {data.submissions.length > 1 ? (
          <Panel className="p-4">
            <p className="mb-2 flex items-center gap-1.5 text-[13px] font-black text-slate-900">
              <History className="h-4 w-4 text-slate-400" />
              تاریخچهٔ ارسال‌ها
            </p>
            <div className="space-y-1.5">
              {data.submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="flex items-center gap-2 rounded-xl border px-3 py-2 text-[11.5px]"
                  style={{ borderColor: HAIRLINE }}
                >
                  <span className="shrink-0 font-black text-slate-400">
                    نسخهٔ {fa(submission.version)}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-bold text-slate-500">
                    {timeAgo(submission.submittedAt)}
                  </span>
                  {submission.review ? (
                    <span
                      className="shrink-0 font-black"
                      style={{
                        color: submission.review.decision === 'APPROVED' ? '#0ca30c' : '#fab219',
                      }}
                    >
                      {submission.review.decision === 'APPROVED' ? 'تأیید' : 'اصلاح'}
                    </span>
                  ) : (
                    <span className="shrink-0 font-bold text-slate-300">بدون بازخورد</span>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        ) : null}
      </div>
    </PortalShell>
  );
};

export default PortalTaskPage;
